<template>
  <v-container
    class="px-0 py-0"
    fluid
  >
    <v-card
      v-if="items"
      class="mt-3 mx-3 bt-1 br-1 bl-1 bc-outlined-gray"
      tile
    >
      <v-card-text class="fs-15 py-4">
        <v-row dense>
          <v-col class="d-flex align-center">
            <p class="mb-0 py-1">
              {{ $bct('Displaying') }}
              <strong>{{ items?.length }}</strong> {{ $bct('out of') }}
              <strong>{{ totalItemCount }}</strong> {{ $bct('total results') }}.
            </p>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <DataTableServer
      v-if="items"
      @update:page="handlePageChange"
      :headers="tableHeaders"
      :hide-default-footer="!totalItemCount || totalItemCount <= itemsPerPage"
      :items="items"
      :items-length="totalItemCount"
      :items-per-page="itemsPerPage"
      :loading="processing"
      class="mx-3 b-radius-0 b-1 bc-outlined-gray"
      item-key="id"
      no-data-text="No statements found"
      fixed-header
      return-object
      show-expand
      show-select
    >
      <template #[`item.id`]="{ item }">
        {{ shortId(item.id) }}
      </template>

      <template #[`item.application_name`]="{ item }">
        <span v-if="APPLICATION_TYPES_WITH_CHILD.includes(item.application_type)">
          {{ item.application_name }}
        </span>
      </template>

      <template #[`item.start_date`]="{ item }">
        <LongDate :date="item.start_date" />
      </template>

      <template #[`item.status`]="{ item }">
        <StandardChip
          v-bind="getInvoiceStatusPresentation(item.status)"
          :text="getStatusDisplayValue(item.status)"
          role="status"
        />
      </template>

      <template #[`item.billing_statement_id`]="{ item }">
        <span>
          {{ item.billing_statement_id?.split('-')[0] }}
        </span>
      </template>

      <template #[`item.amount`]="{ item }">
        <span>
          {{ currency(item.total_amount) }}
        </span>
      </template>

      <template #expanded-row="{ item }">
        <template v-if="item.invoice_line_items.length === 0">
          <tr class="bg-white-smoke">
            <td
              class="ta-center"
              colspan="9"
            >
              <span>
                {{ $bct('No invoice line items') }}
              </span>
            </td>
          </tr>
        </template>

        <template
          v-for="invoiceLineItem in item.invoice_line_items"
          v-else
          :key="invoiceLineItem.amount?.toString() + invoiceLineItem.reason?.toString()"
        >
          <tr class="bg-white-smoke">
            <td colspan="4" />

            <td
              class="d-flex justify-end align-center"
              colspan="1"
            >
              {{ currency(invoiceLineItem.amount) }}
            </td>

            <td colspan="1" />

            <td
              class="justify-end"
              colspan="1"
            >
              {{ $bct(invoiceLineItem.reason) }}
            </td>

            <td colspan="2" />
          </tr>
        </template>
      </template>
    </DataTableServer>

    <VerticalSpacer :min-height="14" />
  </v-container>
</template>

<script setup>
import { currency, shortId } from '@/plugins/filters';
import DataTableServer from '@/shared/components/table/DataTableServer.vue';
import { getInvoiceStatusPresentation, getStatusDisplayValue } from '@/shared/utils/invoiceStatus';
import LongDate from '@/shared/components/LongDate.vue';
import useApi from '@/shared/composables/useApi';
import VerticalSpacer from '@/shared/components/VerticalSpacer.vue';

const $bct = inject('translator');

const { api } = useApi();

const APPLICATION_TYPES_WITH_CHILD = ['Subsidy'];

const items = ref(null);
const invoiceLineItems = ref([]);
const processing = ref(null);
const tableHeaders = ref([
  {
    title: 'Location',
    value: 'business_name',
  },
  {
    title: 'Invoice ID',
    value: 'id',
  },
  {
    title: 'Child',
    value: 'application_name',
  },
  {
    title: 'Invoice date',
    value: 'start_date',
  },
  {
    title: 'Status',
    value: 'status',
    align: 'center',
  },
  {
    title: 'Statement ID',
    value: 'billing_statement_id',
  },
  {
    title: 'Amount',
    value: 'amount',
    align: 'end',
  },
]);
const totalItemCount = ref(null);
const itemsPerPage = ref(null);
const page = ref(1);

async function handlePageChange(newPageValue) {
  page.value = newPageValue;
  await loadInvoices();
}

async function load() {
  await loadInvoiceLineItems();
  await loadInvoices();
}

async function loadInvoiceLineItems() {
  const response = await api.manager.invoice_line_item.index();
  invoiceLineItems.value = response?.data;
}

async function loadInvoices() {
  const params = { page: page.value };
  const response = await api.manager.invoice.index(params);

  items.value = response?.data;

  // Add invoice_line_items to invoices
  items.value.forEach((invoice) => {
    invoice.invoice_line_items = invoiceLineItems.value.filter(
      (invoice_line_item) => invoice_line_item.invoice_id === invoice.id,
    );
  });

  itemsPerPage.value = parseInt(response.headers['x-page-size'] || 0, 10);
  totalItemCount.value = parseInt(response.headers['x-total-count'] || 0, 10);
}

onMounted(load);
</script>
